/* @ts-ignore */
import queryStringHandler from "query-string-handler";
import { scrollToElement } from "@/components/global/scroll-to-element";

interface ResultItem {
  region?: string;
  meetingType?: string;
  title?: string;
  descriptionHtml?: string;
  location?: string;
  dateDisplay?: string;
  link?: LinkItem;
}

interface LinkItem {
  href?: string;
  target?: string;
  text?: string;
  title?: string;
}

export default function EventsIndexPage() {
  const eventsIndex = document.querySelector<HTMLDivElement>(".js-events-index");
  const filtersMenu = document.querySelector<HTMLDivElement>(".js-filters-mobile-menu");
  const fixedMenuWrapper = document.querySelector<HTMLDivElement>(".js-fixed-menu");

  if (eventsIndex) {
    const selectFilters = eventsIndex.querySelectorAll<HTMLSelectElement>(".js-filter-item");
    const resultsWrapper = eventsIndex.querySelector<HTMLDivElement>(".js-results-wrapper");
    const selectFilterBtns = eventsIndex.querySelectorAll<HTMLButtonElement>(".js-filter-btn");
    const resultsList = eventsIndex.querySelector<HTMLDivElement>(".js-results-list");
    const paginationWrapper = eventsIndex.querySelector<HTMLButtonElement>(".js-pagination-wrapper");
    const prevPageBtn = eventsIndex.querySelector<HTMLButtonElement>(".js-prev-btn");
    const nextPageBtn = eventsIndex.querySelector<HTMLButtonElement>(".js-next-btn");
    const clearBtns = eventsIndex.querySelectorAll<HTMLButtonElement>(".js-clear-btn");
    const openBtn = filtersMenu?.querySelector<HTMLButtonElement>(".js-open-menu-btn");
    const cancelBtn = filtersMenu?.querySelector<HTMLButtonElement>(".js-cancel-btn");
    const applyBtn = filtersMenu?.querySelector<HTMLButtonElement>(".js-apply-btn");
    const mobileFilterItems = filtersMenu?.querySelectorAll<HTMLDivElement>(".js-mobile-filter-item");
    const mobileFilterBtns = filtersMenu?.querySelectorAll<HTMLButtonElement>(".js-mobile-filter-btn");
    let page: number = 1;
    let totalPages: number = 0;
    let region: string = "";
    let savedRegion = "";
    let meeting: string = "";
    let savedMeeting = "";
    let savedPage = 0;
    let results: ResultItem[] = [];
    let disabledFacets: string[] = [];

    const hanldeCreateEventCard = (item: ResultItem) => {
      return `
        <article class="js-result-item flex flex-col gap-6 py-6 lg:py-8 border-t first:border-t-0 border-t-neutral-100">
            ${
              item?.meetingType
                ? `
                <div>
                    <div class="inline-flex py-1 px-3 rounded-full bg-primary-100 font-primary text-sm leading-[22px] font-semibold text-primary-700 ">${item.meetingType}</div>
                </div>
                `
                : ""
            }
            ${
              item?.title || item?.descriptionHtml || item?.location || item?.dateDisplay
                ? `
                <div class="flex flex-col gap-4">
                    ${
                      item?.title
                        ? `
                        <h3 class="h4-mobile lg:h4 text-primary-700">${item.title}</h3>
                        `
                        : ""
                    }

                    ${
                      item?.descriptionHtml
                        ? `
                        <div class="rich-text body text-neutral-700">${item.descriptionHtml}</div>
                        `
                        : ""
                    }

                    ${
                      item?.location || item?.dateDisplay
                        ? `
                        <ul class="flex flex-col body-sm text-neutral-700 font-semibold">
                            ${
                              item?.location
                                ? `
                                <li>${item.location}</li>
                                `
                                : ""
                            }

                            ${
                              item?.dateDisplay
                                ? `
                                <li>${item.dateDisplay}</li>
                                `
                                : ""
                            }
                        </ul>
                        `
                        : ""
                    }
                </div>
                `
                : ""
            }
            ${
              item?.link?.href
                ? `
                <div>
                    <a href="${item.link.href}" target="${item.link.target ? item.link.target : "_blank"}" aria-label="${item.link.title}" class="btn btn-primary">${item.link.text ? item.link.text : "Learn More"}</a>
                </div>
                `
                : ""
            }
        </article>
      `;
    };

    const handleNewCards = () => {
      if (results.length > 0) {
        eventsIndex.setAttribute("data-noresults", "false");
        resultsWrapper?.setAttribute("data-results", "true");

        results.forEach((item: ResultItem) => {
          resultsList?.insertAdjacentHTML("beforeend", hanldeCreateEventCard(item));
        });
      } else {
        eventsIndex.setAttribute("data-noresults", "true");
      }
    };

    const handleRemoveCards = () => {
      eventsIndex.setAttribute("data-results", "false");
      const resultItems = resultsList?.querySelectorAll<HTMLDivElement>(".js-result-item");

      if (resultItems && resultItems.length > 0) {
        resultItems.forEach((item) => item.remove());
      }
    };

    const handleUpdateSelectValue = (selectedQuery: string, value: string) => {
      selectFilters.forEach((item) => {
        const menuBtnSelectedText = item.querySelector<HTMLSpanElement>(".js-selected-text");
        const groupQuery = item.getAttribute("data-query");
        const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-filter-btn");

        if (filterBtns && filterBtns.length > 0 && menuBtnSelectedText && groupQuery === selectedQuery.trim()) {
          let selectedIndex = 0;

          filterBtns.forEach((btn, index) => {
            if (btn.getAttribute("data-value") === value) {
              menuBtnSelectedText.innerText = btn.textContent || "";
              selectedIndex = index;
            }
          });

          filterBtns[selectedIndex].disabled = true;
        }
      });

      mobileFilterItems?.forEach((item) => {
        const menuBtnSelectedText = item.querySelector<HTMLSpanElement>(".js-selected-mobile-text");
        const groupQuery = item.getAttribute("data-group-query");
        const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-mobile-filter-btn");

        if (menuBtnSelectedText && filterBtns && filterBtns.length > 0 && groupQuery === selectedQuery.trim()) {
          let selectedIndex = 0;
          filterBtns.forEach((btn, index) => {
            if (btn.getAttribute("data-value") === value) {
              menuBtnSelectedText.innerText = btn.textContent || "";
              selectedIndex = index;
            }
          });

          filterBtns[selectedIndex].disabled = true;
        }
      });
    };

    const handleFetchResults = async (pg: number, r: string, m: string) => {
      try {
        const url = `/api/search/event-detail?pg=${pg}&r=${r}&m=${m}`;

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();
        // clear results array
        results = [];
        handleRemoveCards();

        if (data.items && data.items.length > 0) {
          results = data.items;
          eventsIndex.setAttribute("data-noresults", "false");
        } else {
          eventsIndex.setAttribute("data-noresults", "true");
        }

        if (data.facetsCollections && data.facetsCollections.length > 0) {
          handleDisabledFacetBtns();
          handleUpdateSelectValue("r", r);
          handleUpdateSelectValue("m", m);
          handleUpdateClearBtn();
        }

        totalPages = data.totalPages;

        handleNewCards();
        handleDisabledPagination();
        eventsIndex.setAttribute("data-loading", "false");
      } catch (error) {
        console.error("Error during GET request:", error);
        eventsIndex.setAttribute("data-noresults", "true");
        eventsIndex.setAttribute("data-loading", "false");
      }
    };

    const handleUpdateQuery = (query: string, value: string) => {
      queryStringHandler.replace({ [query]: value });

      if (query === "r") {
        region = value;
      }

      if (query === "m") {
        meeting = value;
      }
      // reset to first page on change
      if (page !== 1 && (query === "r" || query === "m")) {
        page = 1;
        queryStringHandler.replace({ pg: "1" });
      }

      handleFetchResults(page, region, meeting);
    };

    const handleDisabledPagination = () => {
      if (totalPages === 1) {
        paginationWrapper?.setAttribute("data-hide", "true");
      } else {
        paginationWrapper?.setAttribute("data-hide", "false");

        if (prevPageBtn) {
          if (page === 1) {
            prevPageBtn.disabled = true;
          } else {
            prevPageBtn.disabled = false;
          }
        }

        if (nextPageBtn) {
          if (page === totalPages) {
            nextPageBtn.disabled = true;
          } else {
            nextPageBtn.disabled = false;
          }
        }
      }
    };

    const handleClearFilters = () => {
      region = "";
      meeting = "";
      page = 1;
      queryStringHandler.replace({ r: "" });
      queryStringHandler.replace({ m: "" });
      queryStringHandler.replace({ pg: "1" });

      handleFetchResults(page, region, meeting);
    };

    const handleScrollToTop = () => {
      scrollToElement(eventsIndex);
    };

    const handlePreviousPage = async () => {
      page--;
      handleUpdateQuery("pg", page.toString());
      await handleFetchResults(page, region, meeting);
      handleScrollToTop();
    };

    const handleNextPage = async () => {
      page++;
      handleUpdateQuery("pg", page.toString());
      await handleFetchResults(page, region, meeting);
      handleScrollToTop();
    };
    // MOBILE MENU METHODS
    const handleSetSavedData = () => {
      savedRegion = region;
      savedMeeting = meeting;
      savedPage = page;
    };

    const handleOpenMenu = () => {
      if (filtersMenu && fixedMenuWrapper) {
        filtersMenu.setAttribute("data-open", "true");
        document.body.style.overflow = "hidden";
        fixedMenuWrapper.style.height = `${window.innerHeight}px`;
        handleSetSavedData();
      }
    };

    const handleCloseMenu = () => {
      if (filtersMenu && fixedMenuWrapper) {
        filtersMenu.setAttribute("data-open", "false");
        fixedMenuWrapper.style.height = "";
        document.body.style.overflow = "";
      }
    };

    const handleApplyChanges = () => {
      handleCloseMenu();
    };

    const handleResize = () => {
      if (filtersMenu && filtersMenu.getAttribute("data-open") === "true") {
        handleCloseMenu();
      }
    };

    const handleCancelChanges = () => {
      handleCloseMenu();
      if (savedRegion !== region || savedMeeting !== meeting || savedPage !== page) {
        queryStringHandler.replace({ r: savedRegion });
        queryStringHandler.replace({ m: savedMeeting });
        queryStringHandler.replace({ pg: savedPage });
        page = savedPage;

        handleFetchResults(savedPage, savedRegion, savedMeeting);
      }
    };

    const handleDisabledFacetBtns = () => {
      if (selectFilterBtns && selectFilterBtns.length > 0) {
        selectFilterBtns.forEach((btn) => {
          const text = btn.innerText;
          if (disabledFacets.includes(text.trim())) {
            btn.disabled = true;
          } else {
            btn.disabled = false;
          }
        });
      }
      if (mobileFilterBtns && mobileFilterBtns.length > 0) {
        mobileFilterBtns.forEach((btn) => {
          const text = btn.innerText;
          if (disabledFacets.includes(text.trim())) {
            btn.disabled = true;
          } else {
            btn.disabled = false;
          }
        });
      }
    };

    const handleUpdateClearBtn = () => {
      const regionQuery: string = queryStringHandler.getValue("r");
      const meetingQuery: string = queryStringHandler.getValue("m");
      let showClearBtn: string = "false";

      if (regionQuery || meetingQuery) {
        showClearBtn = "true";
      }

      clearBtns.forEach((btn) => {
        btn.setAttribute("data-show", showClearBtn);
      });
    };

    mobileFilterItems?.forEach((item) => {
      const selectedText = item.querySelector<HTMLSpanElement>(".js-selected-mobile-text");
      const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-mobile-filter-btn");
      const accordionBtn = item.querySelector<HTMLButtonElement>(".js-accordion-btn");
      const accordionContentWrapper = item.querySelector<HTMLDivElement>(".js-accordion-content-wrapper");

      const handleCloseAccordion = () => {
        if (accordionBtn && accordionContentWrapper) {
          accordionBtn.setAttribute("aria-expanded", "false");
          accordionContentWrapper.style.maxHeight = "";
        }
      };

      filterBtns.forEach((btn) => {
        const groupQuery = btn.getAttribute("data-query");
        const queryValue = btn.getAttribute("data-value");

        btn.addEventListener("click", () => {
          if (selectedText) {
            selectedText.innerText = btn.textContent || "";
          }
          if (groupQuery) {
            handleUpdateQuery(groupQuery, queryValue || "");
            handleCloseAccordion();
          }
        });
      });
    });

    if (prevPageBtn) {
      prevPageBtn.addEventListener("click", handlePreviousPage);
    }

    if (nextPageBtn) {
      nextPageBtn.addEventListener("click", handleNextPage);
    }

    if (clearBtns && clearBtns.length > 0) {
      clearBtns.forEach((btn) => {
        btn.addEventListener("click", handleClearFilters);
      });
    }

    if (selectFilters) {
      selectFilters.forEach((item) => {
        const menuBtn = item.querySelector<HTMLSelectElement>(".js-select-menu-btn");
        const menuBtnSelectedText = menuBtn?.querySelector<HTMLSpanElement>(".js-selected-text");
        const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-filter-btn");
        const groupQuery = item.getAttribute("data-query");

        const handleTabIndex = () => {
          filterBtns.forEach((btn) => {
            if (item.getAttribute("data-open") === "true") {
              btn.tabIndex = 0;
            } else {
              btn.tabIndex = -1;
            }
          });
        };

        const handleToggleMenu = () => {
          if (item.getAttribute("data-open") === "false") {
            item.setAttribute("data-open", "true");
            handleTabIndex();
          } else {
            item.setAttribute("data-open", "false");
            handleTabIndex();
          }
        };

        const handleUpdateSelectedItem = (value: string) => {
          if (menuBtnSelectedText) {
            menuBtnSelectedText.innerText = value;
          }
        };

        filterBtns.forEach((btn, i) => {
          const value = btn.getAttribute("data-value");
          const text = btn.textContent;

          if (groupQuery && text) {
            btn.addEventListener("click", () => {
              handleUpdateSelectedItem(text);
              handleToggleMenu();
              handleUpdateQuery(groupQuery, value || "");
            });

            btn.addEventListener("keydown", (e: KeyboardEvent) => {
              if (e.code === "Escape") {
                item.setAttribute("data-open", "false");
                menuBtn?.focus();
                handleTabIndex();
              }
            });
          }

          if (i === filterBtns.length - 1) {
            btn.addEventListener("keydown", (e: KeyboardEvent) => {
              if (e.code === "Tab" && !e.shiftKey) {
                item.setAttribute("data-open", "false");
                handleTabIndex();
              }
            });
          }
        });

        if (menuBtn) {
          menuBtn.addEventListener("click", handleToggleMenu);
        }

        document.addEventListener("click", (e: MouseEvent) => {
          const target = e.target as Node;
          if (!item.contains(target) && item.getAttribute("data-open") === "true") {
            item.setAttribute("data-open", "false");
            handleTabIndex();
          }
        });
      });
    }
    // mobile menu actions
    if (openBtn) {
      openBtn.addEventListener("click", handleOpenMenu);
    }

    if (cancelBtn) {
      cancelBtn.addEventListener("click", handleCancelChanges);
    }

    if (applyBtn) {
      applyBtn.addEventListener("click", handleApplyChanges);
    }

    document.addEventListener("resize", handleResize);

    // set values if they're in the query string on load
    document.addEventListener("DOMContentLoaded", () => {
      const pageQuery: number = queryStringHandler.getValue("pg") ? parseInt(queryStringHandler.getValue("pg")) : page;
      const regionQuery: string = queryStringHandler.getValue("r") || region;
      const meetingQuery: string = queryStringHandler.getValue("m") || meeting;

      // on fetch for results if there is a query param that has filters
      if (pageQuery !== page || regionQuery !== region || meetingQuery !== meeting) {
        if (pageQuery !== page) {
          page = pageQuery;
        }

        if (regionQuery !== region) {
          region = regionQuery;
        }

        if (meetingQuery !== meeting) {
          meeting = meetingQuery;
        }

        handleFetchResults(page, region, meeting);
      } else {
        handleDisabledPagination();
        eventsIndex.setAttribute("data-loading", "false");
      }
    });
  }
}
